import { translate as t } from '@/lib/i18n';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { accountsApi, ApiError } from '@/api';
import { accountKeys, analyticsKeys, identityKeys } from '@/lib/queryKeys';
import type { AccountCreateRequest, AccountResponse, Page } from '@/types/api';

export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AccountCreateRequest) => accountsApi.create(body),
    onSuccess: () => {
      toast.success(t('Compte ajouté', 'Account added'));
      qc.invalidateQueries({ queryKey: accountKeys.all });
      qc.invalidateQueries({ queryKey: identityKeys.all });
      qc.invalidateQueries({ queryKey: analyticsKeys.all });
    },
    onError: (err) => {
      const apiErr = ApiError.from(err);
      if (apiErr.isConflict()) {
        toast.error(t('Ce compte existe déjà.', 'This account already exists.'));
      } else {
        toast.error(t("Impossible d'ajouter le compte", 'Unable to add account'), {
          description: apiErr.localizedMessage,
        });
      }
    },
  });
}

export function useBulkImportAccounts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => accountsApi.bulkImport(file),
    onSuccess: (result) => {
      const { created, skipped, errors } = result;
      const parts: string[] = [];
      if (created) parts.push(t(`${created} créé${created > 1 ? 's' : ''}`, `${created} created`));
      if (skipped)
        parts.push(t(`${skipped} ignoré${skipped > 1 ? 's' : ''}`, `${skipped} skipped`));
      if (errors.length)
        parts.push(
          t(
            `${errors.length} erreur${errors.length > 1 ? 's' : ''}`,
            `${errors.length} error${errors.length > 1 ? 's' : ''}`,
          ),
        );
      toast.success(t('Import CSV terminé', 'CSV import complete'), {
        description: parts.join(' · '),
      });
      qc.invalidateQueries({ queryKey: accountKeys.all });
      qc.invalidateQueries({ queryKey: identityKeys.all });
      qc.invalidateQueries({ queryKey: analyticsKeys.all });
    },
    onError: (err) => {
      toast.error(t("Échec de l'import CSV", 'CSV import failed'), {
        description: ApiError.from(err).localizedMessage,
      });
    },
  });
}

export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => accountsApi.remove(id),
    onSuccess: () => {
      toast.success(t('Compte supprimé', 'Account deleted'));
      qc.invalidateQueries({ queryKey: accountKeys.all });
      qc.invalidateQueries({ queryKey: identityKeys.all });
      qc.invalidateQueries({ queryKey: analyticsKeys.all });
    },
    onError: (err) => {
      toast.error(t('Suppression impossible', 'Unable to delete'), {
        description: ApiError.from(err).localizedMessage,
      });
    },
  });
}

export function usePatchAccountIdentity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, identityId }: { id: string; identityId: string | null }) =>
      accountsApi.patchIdentity(id, { identityId }),
    onSuccess: (updated) => {
      toast.success(t('Identité mise à jour', 'Identity updated'));
      qc.setQueryData(accountKeys.detail(updated.id), updated);
      qc.invalidateQueries({ queryKey: accountKeys.all });
      qc.invalidateQueries({ queryKey: identityKeys.all });
    },
    onError: (err) => {
      toast.error(t("Échec de la mise à jour de l'identité", 'Unable to update identity'), {
        description: ApiError.from(err).localizedMessage,
      });
    },
  });
}

// Optimistic toggle of isActive (the Switch in the table flips instantly).
export function usePatchAccountActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      accountsApi.patchActive(id, { isActive }),
    onMutate: async ({ id, isActive }) => {
      await qc.cancelQueries({ queryKey: accountKeys.all });
      const snapshots = qc.getQueriesData<Page<AccountResponse>>({
        queryKey: accountKeys.lists(),
      });
      snapshots.forEach(([key, data]) => {
        if (!data) return;
        qc.setQueryData<Page<AccountResponse>>(key, {
          ...data,
          content: data.content.map((a) => (a.id === id ? { ...a, isActive } : a)),
        });
      });
      const detailSnap = qc.getQueryData<AccountResponse>(accountKeys.detail(id));
      if (detailSnap) {
        qc.setQueryData<AccountResponse>(accountKeys.detail(id), { ...detailSnap, isActive });
      }
      return { snapshots, detailSnap };
    },
    onError: (err, _vars, ctx) => {
      // Rollback
      ctx?.snapshots.forEach(([key, data]) => qc.setQueryData<Page<AccountResponse>>(key, data));
      if (ctx?.detailSnap) qc.setQueryData(accountKeys.detail(ctx.detailSnap.id), ctx.detailSnap);
      toast.error(t('Échec du changement de statut', 'Unable to change status'), {
        description: ApiError.from(err).localizedMessage,
      });
    },
    onSuccess: (updated) => {
      qc.setQueryData(accountKeys.detail(updated.id), updated);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: accountKeys.all });
    },
  });
}
